import React, { useState, useRef, useEffect } from 'react'
import { Button } from 'react-bootstrap'
import { FaCalendarAlt, FaTimes } from 'react-icons/fa'
import moment from 'moment'
import './CustomDateRangePicker.scss'

const CustomDateRangePicker = ({
  dateRange,
  onChange,
  placeholder = "Select date range",
  className = ""
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [startDate, endDate] = dateRange
  const [tempStartDate, setTempStartDate] = useState(startDate)
  const [tempEndDate, setTempEndDate] = useState(endDate)
  const [selectingStart, setSelectingStart] = useState(true)
  const [dropdownPosition, setDropdownPosition] = useState('left')
  const [clickCount, setClickCount] = useState(0)
  const [clickTimer, setClickTimer] = useState(null)
  const [currentMonth, setCurrentMonth] = useState(moment(startDate || undefined))
  const dropdownRef = useRef(null)
  const inputRef = useRef(null)

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false)
        setTempStartDate(startDate)
        setTempEndDate(endDate)
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      calculateDropdownPosition()
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      if (clickTimer) {
        clearTimeout(clickTimer)
      }
    }
  }, [isOpen, startDate, endDate, clickTimer])

  const calculateDropdownPosition = () => {
    if (!inputRef.current) return

    // Use a small delay to ensure DOM has updated
    setTimeout(() => {
      if (!inputRef.current) return // Double check after timeout

      const inputRect = inputRef.current.getBoundingClientRect()
      const viewportWidth = window.innerWidth
      const dropdownWidth = 320 // min-width from CSS
      const buffer = 20 // Buffer from edge

      // Calculate available space on both sides
      const spaceOnRight = viewportWidth - inputRect.left - buffer
      const spaceOnLeft = inputRect.right - buffer

      // Choose positioning based on available space
      if (spaceOnRight >= dropdownWidth) {
        // Enough space on the right - align to left edge of input
        setDropdownPosition('left')
      } else if (spaceOnLeft >= dropdownWidth) {
        // Not enough space on right, but enough on left - align to right edge of input
        setDropdownPosition('right')
      } else {
        // Not enough space on either side - center it
        setDropdownPosition('center')
      }
    }, 50) // Increased delay for better reliability
  }

  const formatDateRange = (start, end) => {
    if (!start && !end) return ""
    if (start && !end) return moment(start).format("DD/MM/YYYY")
    if (start && end) {
      if (moment(start).isSame(moment(end), 'day')) {
        return moment(start).format("DD/MM/YYYY")
      }
      return `${moment(start).format("DD/MM/YYYY")} - ${moment(end).format("DD/MM/YYYY")}`
    }
    return ""
  }

  const getDisplayText = () => {
    const text = formatDateRange(startDate, endDate)
    return text || placeholder
  }

  const handleDateClick = (date, isCurrentMonth = true) => {
    // If clicking a date from adjacent month, navigate to that month first
    if (!isCurrentMonth) {
      setCurrentMonth(moment(date))
      return
    }

    const dateString = moment(date).format('YYYY-MM-DD')

    // Increment click count
    setClickCount(prev => prev + 1)

    if (clickTimer) {
      clearTimeout(clickTimer)
    }

    const timer = setTimeout(() => {
      if (clickCount + 1 === 1) {
        // Single click - normal range selection
        if (selectingStart) {
          setTempStartDate(date)
          setTempEndDate(null)
          setSelectingStart(false)
        } else {
          if (moment(date).isBefore(tempStartDate)) {
            setTempStartDate(date)
            setTempEndDate(tempStartDate)
          } else {
            setTempEndDate(date)
          }
          setSelectingStart(true)
        }
      } else if (clickCount + 1 === 2) {
        // Double click - select single day
        setTempStartDate(date)
        setTempEndDate(date)
        onChange([date, date])
        setIsOpen(false)
        setSelectingStart(true)
      }

      setClickCount(0)
      setClickTimer(null)
    }, 250)

    setClickTimer(timer)
  }


  const handleShortcut = (type) => {
    const today = moment().startOf('day').toDate()
    let start, end

    switch (type) {
      case 'today':
        start = end = today
        break
      case 'thisWeek':
        start = moment().startOf('isoWeek').toDate()
        end = moment().endOf('isoWeek').toDate()
        break
      case 'thisMonth':
        start = moment().startOf('month').toDate()
        end = moment().endOf('month').toDate()
        break
      default:
        return
    }

    setTempStartDate(start)
    setTempEndDate(end)
    onChange([start, end])
    setIsOpen(false)
    setSelectingStart(true)

    // Reset position for next time dropdown opens
    setTimeout(() => setDropdownPosition('left'), 100)
  }

  const handleClear = () => {
    setTempStartDate(null)
    setTempEndDate(null)
    onChange([null, null])
    setIsOpen(false)
    setSelectingStart(true)

    // Reset position for next time dropdown opens
    setTimeout(() => setDropdownPosition('left'), 100)
  }

  const handleApply = () => {
    // If only start date is selected, treat it as a single day
    if (tempStartDate && !tempEndDate) {
      onChange([tempStartDate, tempStartDate])
    } else {
      onChange([tempStartDate, tempEndDate])
    }
    setIsOpen(false)
    setSelectingStart(true)
  }

  const handleCancel = () => {
    setTempStartDate(startDate)
    setTempEndDate(endDate)
    setIsOpen(false)
    setSelectingStart(true)
  }

  const renderCalendar = () => {
    const today = moment()
    const daysInMonth = currentMonth.daysInMonth()
    const startOfMonth = moment(currentMonth).startOf('month')
    const startOfCalendar = moment(startOfMonth).startOf('isoWeek') // isoWeek starts on Monday

    const weeks = []
    let currentWeek = []

    for (let i = 0; i < 42; i++) {
      const day = moment(startOfCalendar).add(i, 'days')
      const isCurrentMonth = day.month() === currentMonth.month()
      const isToday = day.isSame(today, 'day')
      const isSelected = (tempStartDate && day.isSame(moment(tempStartDate), 'day')) ||
                        (tempEndDate && day.isSame(moment(tempEndDate), 'day'))
      const isInRange = tempStartDate && tempEndDate &&
                       day.isBetween(moment(tempStartDate), moment(tempEndDate), 'day', '[]')

      currentWeek.push(
        <div
          key={day.format('YYYY-MM-DD')}
          className={`calendar-day ${isCurrentMonth ? 'current-month' : 'other-month'}
                     ${isToday ? 'today' : ''}
                     ${isSelected ? 'selected' : ''}
                     ${isInRange ? 'in-range' : ''}`}
          onClick={() => handleDateClick(day.toDate(), isCurrentMonth)}
          title={!isCurrentMonth ? `Click to go to ${day.format('MMMM YYYY')}` : ''}
        >
          {day.date()}
        </div>
      )

      if ((i + 1) % 7 === 0) {
        weeks.push(
          <div key={`week-${weeks.length}`} className="calendar-week">
            {currentWeek}
          </div>
        )
        currentWeek = []
      }
    }

    return (
      <div className="calendar">
        <div className="calendar-header">
          <Button
            variant="link"
            className="nav-btn"
            onClick={() => setCurrentMonth(moment(currentMonth).subtract(1, 'month'))}
          >
            ‹
          </Button>
          <span className="month-year">{currentMonth.format('MMMM YYYY')}</span>
          <Button
            variant="link"
            className="nav-btn"
            onClick={() => setCurrentMonth(moment(currentMonth).add(1, 'month'))}
          >
            ›
          </Button>
        </div>
        <div className="calendar-weekdays">
          {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(day => (
            <div key={day} className="weekday-header">{day}</div>
          ))}
        </div>
        <div className="calendar-body">
          {weeks}
        </div>
      </div>
    )
  }

  return (
    <div className={`custom-date-range-picker ${className}`} ref={dropdownRef}>
      <div
        className={`picker-input ${isOpen ? 'open' : ''} ${!getDisplayText() || getDisplayText() === placeholder ? 'empty' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
        ref={inputRef}
      >
        <FaCalendarAlt className="calendar-icon" />
        <span className="date-text">{getDisplayText()}</span>
        {(startDate || endDate) && (
          <FaTimes
            className="clear-icon"
            onClick={(e) => {
              e.stopPropagation()
              handleClear()
            }}
          />
        )}
      </div>

      {isOpen && (
        <div className={`picker-dropdown ${dropdownPosition === 'right' ? 'align-right' : dropdownPosition === 'center' ? 'align-center' : ''}`}>
          <div className="shortcuts">
            <Button variant="outline-primary" size="sm" onClick={() => handleShortcut('today')}>
              Today
            </Button>
            <Button variant="outline-primary" size="sm" onClick={() => handleShortcut('thisWeek')}>
              This Week
            </Button>
            <Button variant="outline-primary" size="sm" onClick={() => handleShortcut('thisMonth')}>
              This Month
            </Button>
            <Button variant="outline-secondary" size="sm" onClick={handleClear}>
              Clear
            </Button>
          </div>

          {renderCalendar()}

          <div className="picker-footer">
            <div className="selection-info">
              {selectingStart
                ? "Select start date (double-click or Apply for single day)"
                : tempStartDate && !tempEndDate
                ? "Select end date or click Apply for single day"
                : "Select end date"}
            </div>
            <div className="btn-group">
              <Button variant="secondary" size="sm" onClick={handleCancel}>Cancel</Button>
              <Button variant="primary" size="sm" onClick={handleApply}>
                {tempStartDate && !tempEndDate ? "Apply (Single Day)" : "Apply"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default CustomDateRangePicker